export const RESIDENT_TRAY_SIZE = 46;
export const RESIDENT_TRAY_MARGIN = 14;
const RESIDENT_TRAY_GAP = 12;
export const RESIDENT_TRAY_PANEL_GAP = 10;
export const RESIDENT_TRAY_PANEL_MAX_WIDTH = 560;
export const RESIDENT_TRAY_PANEL_MIN_WIDTH = 380;
export const RESIDENT_TRAY_PANEL_MIN_SIDECAR_WIDTH = 400;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toRect({ viewportWidth, viewportHeight, size, right, bottom }) {
  return {
    left: viewportWidth - right - size,
    top: viewportHeight - bottom - size,
    right: viewportWidth - right,
    bottom: viewportHeight - bottom,
    width: size,
    height: size,
  };
}

function densityForHeight(height) {
  if (height < 500) return "micro";
  if (height < 600) return "compact";
  if (height < 700) return "cozy";
  return "roomy";
}

function estimatedPanelHeight(density) {
  if (density === "micro") return 398;
  if (density === "compact") return 430;
  if (density === "cozy") return 486;
  return 520;
}

function expandedRect(rect, pad) {
  return {
    left: rect.left - pad,
    top: rect.top - pad,
    right: rect.right + pad,
    bottom: rect.bottom + pad,
  };
}

function overlapArea(a, b, pad = 0) {
  const aa = pad ? expandedRect(a, pad) : a;
  const width = Math.max(0, Math.min(aa.right, b.right) - Math.max(aa.left, b.left));
  const height = Math.max(0, Math.min(aa.bottom, b.bottom) - Math.max(aa.top, b.top));
  return width * height;
}

function rectOverlapsAny(rect, avoidRects, pad) {
  return avoidRects.some((avoidRect) => overlapArea(rect, avoidRect, pad) > 0);
}

function placementScore(rect, avoidRects, pad) {
  return avoidRects.reduce((total, avoidRect) => total + overlapArea(rect, avoidRect, pad), 0);
}

export function computeResidentTrayPlacement({
  viewportWidth,
  viewportHeight,
  avoidRects = [],
  size = RESIDENT_TRAY_SIZE,
  margin = RESIDENT_TRAY_MARGIN,
  gap = RESIDENT_TRAY_GAP,
  pad = 8,
} = {}) {
  const vw = Number(viewportWidth) || 0;
  const vh = Number(viewportHeight) || 0;
  const maxRight = Math.max(margin, vw - size - 8);
  const maxBottom = Math.max(margin, vh - size - 8);
  const maxRows = Math.max(0, Math.min(8, Math.floor((maxBottom - margin) / (size + gap))));
  const maxColumns = vw >= 720 ? Math.max(0, Math.min(4, Math.floor((maxRight - margin) / (size + gap)))) : 0;
  const seen = new Set();
  let best = null;

  for (let column = 0; column <= maxColumns; column += 1) {
    for (let row = 0; row <= maxRows; row += 1) {
      const right = clamp(margin + column * (size + gap), margin, maxRight);
      const bottom = clamp(margin + row * (size + gap), margin, maxBottom);
      const key = `${right}:${bottom}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const rect = toRect({ viewportWidth: vw, viewportHeight: vh, size, right, bottom });
      if (!rectOverlapsAny(rect, avoidRects, pad)) {
        return { right, bottom, shifted: row > 0 || column > 0, rect };
      }
      const score = placementScore(rect, avoidRects, pad);
      if (!best || score < best.score) {
        best = { right, bottom, shifted: row > 0 || column > 0, rect, score };
      }
    }
  }

  return best
    ? { right: best.right, bottom: best.bottom, shifted: best.shifted, rect: best.rect }
    : { right: margin, bottom: margin, shifted: false, rect: toRect({ viewportWidth: vw, viewportHeight: vh, size, right: margin, bottom: margin }) };
}

export function computeResidentTrayPanelLayout({
  viewportWidth,
  viewportHeight,
  placement = {},
  size = RESIDENT_TRAY_SIZE,
  margin = RESIDENT_TRAY_MARGIN,
  gap = RESIDENT_TRAY_PANEL_GAP,
  minWidth = RESIDENT_TRAY_PANEL_MIN_WIDTH,
  minSidecarWidth = RESIDENT_TRAY_PANEL_MIN_SIDECAR_WIDTH,
  maxWidth = RESIDENT_TRAY_PANEL_MAX_WIDTH,
} = {}) {
  const vw = Number(viewportWidth) || 0;
  const vh = Number(viewportHeight) || 0;
  const safeWidth = Math.max(0, vw - margin * 2);
  const safeHeight = Math.max(0, vh - margin * 2);
  const density = densityForHeight(safeHeight);
  const emergencyOverflow = safeHeight < 470 || safeWidth < minWidth;
  const desiredMaxWidth = vw >= 1180 ? maxWidth : (vw >= 840 ? Math.min(maxWidth, 540) : Math.min(maxWidth, 520));
  const fallbackWidth = clamp(safeWidth, 0, desiredMaxWidth);
  const orbRect = toRect({
    viewportWidth: vw,
    viewportHeight: vh,
    size,
    right: Number.isFinite(placement.right) ? placement.right : margin,
    bottom: Number.isFinite(placement.bottom) ? placement.bottom : margin,
  });
  const preferredHeight = Math.min(estimatedPanelHeight(density), safeHeight);
  const centerY = orbRect.top + orbRect.height / 2;
  const fitTop = (height = preferredHeight) => clamp(centerY - height / 2, margin, Math.max(margin, vh - margin - height));
  const leftSideWidth = Math.min(desiredMaxWidth, orbRect.left - gap - margin);
  const rightSideWidth = Math.min(desiredMaxWidth, vw - margin - gap - orbRect.right);

  if (leftSideWidth >= minSidecarWidth) {
    const width = Math.max(minWidth, leftSideWidth);
    const top = fitTop();
    return {
      placement: "sidecar-left",
      density,
      left: Math.max(margin, orbRect.left - gap - width),
      top,
      width,
      maxHeight: Math.max(0, vh - margin - top),
      overflowY: emergencyOverflow ? "auto" : "visible",
      emergencyOverflow,
    };
  }

  if (rightSideWidth >= minSidecarWidth) {
    const width = Math.max(minWidth, rightSideWidth);
    const top = fitTop();
    return {
      placement: "sidecar-right",
      density,
      left: Math.min(vw - margin - width, orbRect.right + gap),
      top,
      width,
      maxHeight: Math.max(0, vh - margin - top),
      overflowY: emergencyOverflow ? "auto" : "visible",
      emergencyOverflow,
    };
  }

  const width = fallbackWidth;
  const centeredLeft = clamp(orbRect.left + orbRect.width / 2 - width / 2, margin, Math.max(margin, vw - margin - width));
  const availableAbove = orbRect.top - gap - margin;
  const availableBelow = vh - margin - gap - orbRect.bottom;
  const canFitAbove = availableAbove >= preferredHeight;
  const canFitBelow = availableBelow >= preferredHeight;

  if (canFitAbove || availableAbove >= availableBelow) {
    return {
      placement: "above",
      density,
      left: centeredLeft,
      top: Math.max(margin, orbRect.top - gap - preferredHeight),
      width,
      maxHeight: Math.max(0, availableAbove),
      overflowY: emergencyOverflow ? "auto" : "visible",
      emergencyOverflow,
    };
  }

  if (canFitBelow || availableBelow > 0) {
    return {
      placement: "below",
      density,
      left: centeredLeft,
      top: Math.min(vh - margin, orbRect.bottom + gap),
      width,
      maxHeight: Math.max(0, availableBelow),
      overflowY: emergencyOverflow ? "auto" : "visible",
      emergencyOverflow,
    };
  }

  return {
    placement: "centered",
    density,
    left: Math.max(margin, (vw - width) / 2),
    top: margin,
    width,
    maxHeight: safeHeight,
    overflowY: "auto",
    emergencyOverflow: true,
  };
}

const AVOID_SELECTOR = [
  "[data-resident-tray-avoid]",
  ".cf-toast",
  "[role='alert']",
  "[role='status']",
  "[aria-live]",
  "button",
  "a[href]",
  "input",
  "textarea",
  "select",
  "summary",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
  "[role='button']",
  "[role='link']",
  "[role='tab']",
  "[role='menuitem']",
].join(",");

function visibleRectForElement(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return null;
  const rect = el.getBoundingClientRect();
  if (!rect || rect.width < 4 || rect.height < 4) return null;
  const style = typeof window !== "undefined" && window.getComputedStyle ? window.getComputedStyle(el) : null;
  if (style) {
    if (style.display === "none" || style.visibility === "hidden") return null;
    if (Number(style.opacity) <= 0.04) return null;
  }
  return rect;
}

export function collectResidentTrayAvoidRects(rootEl, {
  viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0,
  viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0,
  size = RESIDENT_TRAY_SIZE,
  margin = RESIDENT_TRAY_MARGIN,
  maxRects = 220,
} = {}) {
  if (typeof document === "undefined") return [];
  const vw = Number(viewportWidth) || 0;
  const vh = Number(viewportHeight) || 0;
  const horizontalReach = size * 6 + margin * 2;
  const verticalReach = size * 9 + margin * 2;
  const minRelevantLeft = Math.max(0, vw - horizontalReach);
  const minRelevantTop = Math.max(0, vh - verticalReach);
  const nodes = Array.from(document.querySelectorAll(AVOID_SELECTOR));
  const rects = [];

  for (const node of nodes) {
    if (rootEl && (node === rootEl || rootEl.contains(node))) continue;
    if (node.closest?.("[data-hermes-resident-tray='true']")) continue;
    const rect = visibleRectForElement(node);
    if (!rect) continue;
    if (rect.right <= 0 || rect.bottom <= 0 || rect.left >= vw || rect.top >= vh) continue;
    if (rect.right < minRelevantLeft && rect.bottom < minRelevantTop) continue;
    const isExplicitAvoid = node.matches?.("[data-resident-tray-avoid], .cf-toast, [role='alert'], [role='status'], [aria-live]");
    if (!isExplicitAvoid && rect.width > vw * 0.72 && rect.height > vh * 0.72) continue;
    rects.push({ left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height });
    if (rects.length >= maxRects) break;
  }

  return rects;
}
